import '@css/app.css';
import './bootstrap';
import { createInertiaApp } from '@inertiajs/react';
import type { ResolvedComponent } from '@inertiajs/react';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import React from 'react';

import { preloadDynamicIcons } from '@/components/ui/preloadDynamicIcons';
import { authenticatedPage } from '@/layouts/authenticatedPage';
import { getPathAndName } from '@/lib/getPathAndName';
import { getInertiaRoot } from '@/lib/inertiaRoot';
import { logger } from '@/lib/logger';
import { skipSelfReportingErrorBoundaries } from '@/lib/reactErrorBoundaryReporting';
import { initSentry, Sentry } from '@/lib/sentry';

const appName = import.meta.env.VITE_APP_NAME || 'Diagonal';

interface PageModule {
  default: ResolvedComponent;
}

if (import.meta.env.DEV && import.meta.env.VITE_STAGEWISE_ENABLED === 'true') {
  import('@stagewise/toolbar')
    .then(({ initToolbar }) => {
      initToolbar();
      logger.info('Stagewise toolbar enabled.');
    })
    .catch(() => {
      logger.warn('Stagewise toolbar not available');
    });
}

async function boot() {
  void initSentry();
  preloadDynamicIcons();

  if (import.meta.env.DEV && import.meta.env.VITE_REACT_SCAN_ENABLED === 'true') {
    import('react-scan')
      .then(({ scan }) => {
        scan({ enabled: true });
      })
      .catch(() => {
        logger.warn('React Scan not available');
      });
  }

  createInertiaApp({
    title: (title) => (title.includes(appName) ? title : `${title} - ${appName}`),
    resolve: async (pageName) => {
      const [path, name] = getPathAndName(pageName);
      const page = (await resolvePageComponent(
        `${path}${name}.tsx`,
        import.meta.glob([
          './pages/**/*.tsx',
          `../../app/**/resources/js/pages/**/*.tsx`,
          '!**/*.test.tsx',
          '!**/*.spec.tsx'
        ])
      )) as PageModule;

      const component = page.default;

      if (!component.layout) {
        component.layout = (page: React.ReactNode) => authenticatedPage(page);
      }

      return component;
    },
    setup({ el, App, props }) {
      const root = getInertiaRoot(el, {
        onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
          logger.error('[React] Uncaught error', {
            error,
            context: { componentStack: errorInfo.componentStack }
          });
        }),
        onCaughtError: skipSelfReportingErrorBoundaries(
          Sentry.reactErrorHandler((error, errorInfo) => {
            logger.error('[React] Caught error', {
              error,
              context: { componentStack: errorInfo.componentStack }
            });
          })
        ),
        onRecoverableError: Sentry.reactErrorHandler((error, errorInfo) => {
          logger.error('[React] Recoverable error', {
            error,
            context: { componentStack: errorInfo.componentStack }
          });
        })
      });
      root.render(
        <React.StrictMode>
          <App {...props} />
        </React.StrictMode>
      );
    },
    progress: {
      color: '#4B5563'
    }
  });
}

void boot();
